/**
 * RecordAMLAfterMLCOClear.jsx: BM-H3733 (5 Oct 2026).
 *
 * Vanessa cleared the MLCO item on BL.RS.006.1537 and expected AML to move.
 * It didn't: the only Thirdfort review on file was a fee earner's
 * "referred", which blocks AML until someone records a fresh review, and no
 * MLCO screen said so. Her choice on the decision card was "Prompt me": once
 * the MLCO has cleared, ask whether to record AML as approved in her name.
 * Never automatic.
 *
 * Renders nothing unless the matter's Thirdfort review is still referred.
 * Records through the same /thirdfort-report-checked route as the matter
 * page, so the same affirmation is required, the reviewer is the signed-in
 * user, and the golden rule chain still applies. A refusal is shown here.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

export default function RecordAMLAfterMLCOClear({ matterRef, headers, onDismiss }) {
  const [ob, setOb] = useState(null);
  const [affirmed, setAffirmed] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const hdrs = useCallback(() => (typeof headers === "function" ? headers() : {}), [headers]);

  useEffect(() => {
    if (!matterRef) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/onboarding/status/${encodeURIComponent(matterRef)}`,
          { headers: hdrs() },
        );
        if (!cancelled) setOb(res.data || null);
      } catch (err) {
        // No onboarding record means there is no AML review to record.
        if (!cancelled) setOb(null);
      }
    })();
    return () => { cancelled = true; };
  }, [matterRef, hdrs]);

  const record = useCallback(async () => {
    if (!affirmed) {
      setError("Confirm you have read the Thirdfort report before approving.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("outcome", "approved");
      form.append("affirm_read", "true");
      if (note.trim()) form.append("notes", note.trim());
      await axios.post(
        `${API}/onboarding/${encodeURIComponent(matterRef)}/thirdfort-report-checked`,
        form,
        { headers: hdrs() },
      );
      setDone(true);
    } catch (err) {
      setError(errorMessage(err, "Could not record AML as approved."));
    } finally {
      setBusy(false);
    }
  }, [affirmed, note, matterRef, hdrs]);

  if (!matterRef || !ob) return null;
  if (!done && (ob.thirdfort_report_outcome || "").toLowerCase() !== "referred") return null;

  if (done) {
    return (
      <div className="rounded-md border border-emerald-700/50 bg-emerald-900/20 p-3 text-sm text-emerald-100" data-testid="mlco-record-aml-done">
        AML recorded as approved on {matterRef}, in your name.
      </div>
    );
  }

  const by = ob.thirdfort_report_checked_by ? ` by ${ob.thirdfort_report_checked_by}` : "";
  return (
    <div className="rounded-md border border-amber-700/50 bg-amber-900/20 p-3 space-y-2 text-sm text-amber-100" data-testid="mlco-record-aml-prompt">
      <div>
        The Thirdfort report on {matterRef} is still recorded as referred{by}, so AML
        is not satisfied. Do you also want to record AML as approved, in your name?
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={affirmed}
          onChange={(e) => setAffirmed(e.target.checked)}
          data-testid="mlco-record-aml-affirm"
        />
        I have read the Thirdfort report.
      </label>
      <textarea
        className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
        rows={2}
        placeholder="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="mlco-record-aml-note"
      />
      {error && <div className="text-xs text-red-300" data-testid="mlco-record-aml-error">{error}</div>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={record}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-700/30 text-emerald-200 border border-emerald-700/60 hover:bg-emerald-700/50 disabled:opacity-50"
          data-testid="mlco-record-aml-confirm"
        >
          Record AML as approved
        </button>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="px-3 py-1.5 rounded-md text-xs border border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="mlco-record-aml-dismiss"
          >
            Not now
          </button>
        )}
      </div>
    </div>
  );
}
